import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useWindowSize } from 'react-use';

import sdk from 'graphql/graphQLClient';
import Layout from 'layouts/Layout';
import LayoutSection from 'layouts/LayoutSection';

import type { IWebsitePageRecord } from 'graphql/types';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';

interface WebsitePageProps {
  pageProps: IWebsitePageRecord;
}

export const getStaticPaths: GetStaticPaths = async ({ locales = ['en-US'] }) => {
  const { allWebsitePages } = await sdk(false).WebsitePageAllSlugs();

  const allPages = allWebsitePages || [];

  const paths = allPages.flatMap(page => {
    const slug = page?.slug;

    return locales?.map(locale => ({
      params: {
        // since this file uses catch-all routes, returning an empty array generates the homepage
        // see @https://nextjs.org/docs/routing/dynamic-routes#optional-catch-all-routes
        slug: !slug || slug === '' ? [] : slug.split('/'),
        id: page?.id || '',
      },
      locale,
    }));
  });

  return {
    paths,
    // 'blocking' will allow for ISR but cause a slight delay for pages that haven't been built before
    // see @https://stackoverflow.com/a/67787457
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<WebsitePageProps> = async ({ params, preview = false }) => {
  const requestedSlug = (params?.slug as string[])?.join('/') || '';

  const { allWebsitePages } = await sdk(preview).WebsitePageData({ slug: requestedSlug });

  const templatePageItems = allWebsitePages || [];

  return {
    props: {
      pageProps: templatePageItems[0] as IWebsitePageRecord,
    },
    notFound: !templatePageItems || templatePageItems.length === 0,
    revalidate: 60,
  };
};

const TemplatePage: NextPage<WebsitePageProps> = ({ pageProps: { slug, metaTags, components } }) => {
  // if (!metaTags) {
  //   throw new Error(`Page at ${slug} is missing SEO`);
  // }
  // if we want to get windowSize, it's more efficient to call hook once per page rather than within each section/component
  const windowSize = useWindowSize();

  const [utmParams, setUtmParams] = useState({});
  const router = useRouter();

  // Use the useEffect hook to read UTM parameters from the URL and update state
  useEffect(() => {
    const { query } = router;
    const utmSource = query.utm_source as string | undefined;
    const utmMedium = query.utm_medium as string | undefined;
    const utmCampaign = query.utm_campaign as string | undefined;

    // Update state with the UTM parameters from the URL
    setUtmParams({
      utm_source: utmSource || '',
      utm_medium: utmMedium || '',
      utm_campaign: utmCampaign || '',
    });

    utmParams;

    // console.log(utmParams);

    // Set UTM parameters in session storage
    sessionStorage.setItem('utm_source', utmSource || '');
    sessionStorage.setItem('utm_medium', utmMedium || '');
    sessionStorage.setItem('utm_campaign', utmCampaign || '');
  }, [router.query]); // Dependency on router.query to update when URL parameters change

  return (
    <Layout slug={slug || ''} seoMetadata={metaTags as any}>
      {components &&
        components.map(
          component =>
            component &&
            !!Object.keys(component).length && (
              <>
                <LayoutSection {...component} windowSize={windowSize} key={component?.id} />
              </>
            ),
        )}
    </Layout>
  );
};

export default TemplatePage;
